import React from "react";
import ReactApexChart from "react-apexcharts";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";

const PieChart = ({
  labels = [],
  series = [],
  height = 320,
  donut = false,
  title,
  labelFontSize = 18,
  legendFontSize = 15,
  tooltipFontSize = 20,
  showTotal = false,
  totalLabel = "Total",
  totalFontSize = 16,
  totalAlign = "center",
  chartId
}) => {
  const theme = useTheme();

  const numberFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
    []
  );

  const chartLabels = labels.length ? labels : ["A", "B", "C"];
  const chartSeries = series.length ? series : [44, 33, 23];

    const totalValue = React.useMemo(
    () =>
      Array.isArray(chartSeries)
        ? chartSeries.reduce((sum, v) => sum + Number(v || 0), 0)
        : 0,
    [chartSeries]
  );

  const options = {
    labels: chartLabels,
    chart: { type: donut ? "donut" : "pie", toolbar: { show: false }, id: chartId, destroyBeforeMount: true },
    theme: { mode: theme.palette.mode },
    colors: [
      "#425078",
      "#d7e7fc",
      "#b7b5b5",
      theme.palette.info.main,
      theme.palette.error.main,
      theme.palette.secondary.main
    ],
    plotOptions: {
      pie: {
        dataLabels: { offset: -30, }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const label = opts.w.globals.labels[opts.seriesIndex];
        const percent = typeof val === "number" ? val.toFixed(0) : val;
        return `${label} - ${percent}%`;
      },
      style: {
        fontSize: `${labelFontSize}px`,
        fontWeight: 600,
        colors: [
          theme.palette.mode === "dark"
            ? theme.palette.common.white
            : theme.palette.text.primary
        ]
      },
      dropShadow: { enabled: false }
    },
    stroke: { colors: [theme.palette.background.paper] },
    tooltip: {
       y: {
        formatter: (val) => numberFormatter.format(Number(val))
      },
      style: { fontSize: `${tooltipFontSize}px` },
      theme: 'light',
      fillSeriesColor: false,
      marker: { show: true },
      onDatasetHover: { highlightDataSeries: false },
      // Apex uses CSS variables; theme:'light' ensures white bg. These colors help in both modes.
      custom: undefined
    },
    legend: {
      show: true,
      position: "bottom",
      labels: { colors: theme.palette.text.secondary },
      fontSize: `${legendFontSize}px`
    },
    title: {
      text: title ?? "",
      style: { color: theme.palette.text.primary, fontSize: `${legendFontSize + 1}px` }
    },
    subtitle: { text: "" },
    responsive: [{ breakpoint: 600, options: { chart: { height: Math.min(260, height) } } }]
  };

  return (
    <Box>
      <ReactApexChart
        options={options}
        series={chartSeries}
        type={donut ? "donut" : "pie"}
        height={height}
      />
      {showTotal && (
        <Box sx={{ mt: 1, textAlign: totalAlign }}>
          <Typography variant="subtitle2" sx={{ fontSize: totalFontSize, fontWeight: 600, color: theme.palette.text.secondary}}>
            {totalLabel}: {numberFormatter.format(totalValue)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PieChart;